import {isFeatureEnabled} from 'feature-flags';
import {
  ConfirmDialog,
  IconButton,
  useDialog,
  openSuccessDialog,
  closeCurrentDialog,
  Action,
  Actions,
  DropdownActionMenuItem,
} from 'platform/components';
import {Hide, HStack, Show} from 'platform/foundation';

import {Link, matchPath, useLocation} from 'react-router-dom';

import {head, isNotNil, pipe, replace} from 'ramda';

import {
  SourcingVehicleListItemResponseBody,
  useAuctionBulkCreateUserComparisonMutation,
  useAuctionBulkCreateUserFavouriteMutation,
  useAuctionBulkCreateUserHiddenMutation,
  useAuctionBulkCreateUserToBuyMutation,
  useAuctionBulkDeleteUserComparisonMutation,
  useAuctionBulkDeleteUserFavouriteMutation,
  useAuctionBulkDeleteUserHiddenMutation,
  useAuctionBulkDeleteUserToBuyMutation,
  useLazySourcingGetAdServerRedirectQuery,
  useSourcingCreateUserCarvagoBuyMutation,
  useSourcingBulkCreateUserComparisonMutation,
  useSourcingBulkCreateUserFavouriteMutation,
  useSourcingBulkCreateUserHiddenMutation,
  useSourcingBulkCreateUserToBuyMutation,
  useSourcingBulkDeleteUserComparisonMutation,
  useSourcingBulkDeleteUserFavouriteMutation,
  useSourcingBulkDeleteUserHiddenMutation,
  useSourcingBulkDeleteUserToBuyMutation,
} from '@dms/api/core';
import {CountryResponseBody} from '@dms/api/shared';
import featureFlags from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {sourcingRoutes, testIds} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {
  buildArray,
  capitalize,
  openInNewTab,
  suffixTestId,
  TestIdProps,
  composePath,
  useNavigate,
} from 'shared/web';

import {CARVAGO_TRANSACTION_WORKSPACE_URLS} from '../../constants/carvagoTransactionWorkspaceUrls';
import {PREV_URL_COOKIE} from '../../constants/PREV_URL_COOKIE';
import {useCreatePriceReport} from '../../hooks/useCreatePriceReport';
import {useSourcingCurrency} from '../../hooks/useSourcingCurrency';
import {useSourcingPage} from '../../hooks/useSourcingPage';
import {useSourcingTargetCountry} from '../../hooks/useSourcingTargetCountry';
import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {SourcingPage} from '../../types/SourcingPage';
import {AdServerRelativeFiltersDropdown} from '../AdServerRelativeFiltersDropdown/AdServerRelativeFiltersDropdown';
import {useRelativeFiltersPresetEditor} from '../RelativeFiltersPresetEditor/useRelativeFiltersPresetEditor';
import {SourcingCarvagoBuyDialog} from '../SourcingCarvagoBuyDialog/SourcingCarvagoBuyDialog';

interface ActionButtonsVehicleProps extends TestIdProps {
  vehicle: SourcingVehicleListItemResponseBody;
  isDisabled?: boolean;
  onDetailOpen?: () => void;
}

export function ActionButtonsVehicle(props: ActionButtonsVehicleProps) {
  const navigate = useNavigate();
  const {pathname} = useLocation();
  const sourcingType = useSourcingType();
  const sourcingPage = useSourcingPage();
  const targetCountry = useSourcingTargetCountry();
  const sourcingCurrency = useSourcingCurrency();
  const {createPriceReport, isPriceReportCreating} = useCreatePriceReport(
    props.vehicle.sourcingVehicle.adId
  );
  const [createSourcingFavourite] = useSourcingBulkCreateUserFavouriteMutation();
  const [deleteSourcingFavourite] = useSourcingBulkDeleteUserFavouriteMutation();
  const [createAuctionFavourite] = useAuctionBulkCreateUserFavouriteMutation();
  const [deleteAuctionFavourite] = useAuctionBulkDeleteUserFavouriteMutation();
  const [createSourcingComparison] = useSourcingBulkCreateUserComparisonMutation();
  const [deleteSourcingComparison] = useSourcingBulkDeleteUserComparisonMutation();
  const [createAuctionComparison] = useAuctionBulkCreateUserComparisonMutation();
  const [deleteAuctionComparison] = useAuctionBulkDeleteUserComparisonMutation();
  const [createSourcingHidden] = useSourcingBulkCreateUserHiddenMutation();
  const [deleteSourcingHidden] = useSourcingBulkDeleteUserHiddenMutation();
  const [createAuctionHidden] = useAuctionBulkCreateUserHiddenMutation();
  const [deleteAuctionHidden] = useAuctionBulkDeleteUserHiddenMutation();
  const [createSourcingToBuy] = useSourcingBulkCreateUserToBuyMutation();
  const [deleteSourcingToBuy] = useSourcingBulkDeleteUserToBuyMutation();
  const [createAuctionToBuy] = useAuctionBulkCreateUserToBuyMutation();
  const [deleteAuctionToBuy] = useAuctionBulkDeleteUserToBuyMutation();
  const [createSourcingCarvagoBuy] = useSourcingCreateUserCarvagoBuyMutation();
  const [getAdServerRedirect] = useLazySourcingGetAdServerRedirectQuery();
  const [isOpen, open, close] = useDialog();
  const [isCarvagoBuyOpen, openCarvagoBuy, closeCarvagoBuy] = useDialog();
  const relativeFiltersPresetEditor = useRelativeFiltersPresetEditor(props);

  const isCurrentDetailPage =
    pathname ===
    composePath(
      sourcingType === SourcingType.AUCTIONS
        ? sourcingRoutes.auctionDetail
        : sourcingRoutes.vehicleDetail,
      {
        params: {adId: props.vehicle?.sourcingVehicle?.adId},
        isSearchParamsPreserved: false,
      }
    );
  const isAnyDetailPage =
    matchPath({path: sourcingRoutes.vehicleDetail}, pathname) ||
    matchPath({path: sourcingRoutes.auctionDetail}, pathname);

  const isVehicleHidden = sourcingPage === SourcingPage.HIDDEN_PAGE || props.vehicle?.hidden;
  const targetCountryCode = targetCountry?.code;
  const adServerReadable = pipe(
    replace('_', '.'),
    capitalize
  )(props.vehicle.sourcingVehicle.server ?? '');

  const handleDetailButtonClick = () => {
    props.onDetailOpen?.();

    if (!isAnyDetailPage) {
      localStorage.setItem(PREV_URL_COOKIE, pathname);
    }
  };

  const callBulkAction = (
    sourcingMutation: (arg: {body: {externalIds: string[]}}) => {unwrap: () => Promise<unknown>},
    auctionMutation: (arg: {body: {externalIds: string[]}}) => {
      unwrap: () => Promise<unknown>;
    }
  ) => {
    const externalIds = [props.vehicle?.sourcingVehicle?.adId];
    return sourcingType === SourcingType.AUCTIONS
      ? auctionMutation({body: {externalIds}})
      : sourcingMutation({body: {externalIds}});
  };

  const handleHideButtonClick = () => {
    isVehicleHidden
      ? isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
        callBulkAction(deleteSourcingHidden, deleteAuctionHidden).unwrap().catch(handleApiError)
      : open();
  };

  const handleConfirmHide = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(createSourcingHidden, createAuctionHidden).unwrap().catch(handleApiError);
  };

  const handleComparisonButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.comparison ? deleteSourcingComparison : createSourcingComparison,
        props.vehicle?.comparison ? deleteAuctionComparison : createAuctionComparison
      )
        .unwrap()
        .catch(handleApiError);
  };

  const handleFavouriteButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.favourite ? deleteSourcingFavourite : createSourcingFavourite,
        props.vehicle?.favourite ? deleteAuctionFavourite : createAuctionFavourite
      )
        .unwrap()
        .catch(handleApiError);
  };

  const handleShopButtonClick = () => {
    isNotNil(props.vehicle?.sourcingVehicle?.adId) &&
      callBulkAction(
        props.vehicle?.inCart ? deleteSourcingToBuy : createSourcingToBuy,
        props.vehicle?.inCart ? deleteAuctionToBuy : createAuctionToBuy
      )
        .unwrap()
        .catch(handleApiError);
  };

  const handleCarvagoBuyButtonClick = () => {
    openCarvagoBuy();
  };

  const handleConfirmCarvagoBuy = (_message: string) => {
    if (!isNotNil(props.vehicle?.sourcingVehicle?.adId)) {
      return;
    }
    createSourcingCarvagoBuy({
      createUserCarvagoBuyRequestBody: {externalId: props.vehicle.sourcingVehicle.adId},
    })
      .unwrap()
      .then(() =>
        openSuccessDialog({
          id: 'sourcing-carvago-buy-success-dialog',
          heading: i18n.t('entity.sourcing.notifications.carvagoBuySentTitle'),
          subheading: i18n.t('entity.sourcing.notifications.carvagoBuySentToCarvago', {count: 1}),
          secondaryButtonLabel: i18n.t('general.actions.trackRequest'),
          onSecondaryButtonClick: () => {
            const url =
              process.env.ENV_TYPE === 'prod'
                ? CARVAGO_TRANSACTION_WORKSPACE_URLS.prod
                : CARVAGO_TRANSACTION_WORKSPACE_URLS.other;
            openInNewTab(url);
          },
          buttonLabel: i18n.t('general.actions.close'),
          onClose: closeCurrentDialog,
          'data-testid': testIds.sourcing.classifieds(
            'actionButtonsVehicle-carvagoBuySuccessDialog'
          ),
        })
      )
      .catch(handleApiError);
    closeCarvagoBuy();
  };

  const handleAdServerOpen = (
    externalId: string,
    serverName: string,
    targetCountry: CountryResponseBody | null,
    relativeFilterId?: string
  ) =>
    getAdServerRedirect({
      externalId,
      serverName,
      sellerCountry: targetCountry?.code,
      currency: sourcingCurrency?.code,
      relativeFilterId,
    })
      .unwrap()
      .then((response) => openInNewTab(response?.url))
      .catch(handleApiError);

  const createAdServerSearchMenuItem = ({
    label,
    serverName,
    testId,
  }: {
    label: string;
    serverName: string;
    testId: string;
  }): DropdownActionMenuItem => ({
    leftIcon: 'content/filter_list',
    label,
    ...(isFeatureEnabled(featureFlags.SOURCING_SAVED_FILTERS)
      ? {
          submenuContent: (closeDropdown) => (
            <AdServerRelativeFiltersDropdown
              onOpenWithPreset={(presetId) =>
                handleAdServerOpen(
                  props.vehicle.sourcingVehicle.adId,
                  serverName,
                  targetCountry,
                  presetId
                )
              }
              onCreatePreset={relativeFiltersPresetEditor.openCreate}
              onEditPreset={relativeFiltersPresetEditor.openEdit}
              onClose={closeDropdown}
              data-testid={suffixTestId(testId, props)}
            />
          ),
        }
      : {
          onClick: () =>
            handleAdServerOpen(props.vehicle.sourcingVehicle.adId, serverName, targetCountry),
        }),
  });

  const menuItems = buildArray<DropdownActionMenuItem>()
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE, {
      leftIcon: 'action/search',
      label: i18n.t('general.actions.showDetail'),
      onClick: () => {
        navigate(
          composePath(
            sourcingType === SourcingType.AUCTIONS
              ? sourcingRoutes.auctionDetail
              : sourcingRoutes.vehicleDetail,
            {
              params: {adId: props.vehicle?.sourcingVehicle?.adId},
            }
          )
        );
        handleDetailButtonClick();
      },
    })
    .when(isNotNil(props.vehicle.sourcingVehicle.url?.full), {
      leftIcon: 'action/open_in_new',
      label: `${i18n.t('general.actions.openResultOn')} ${adServerReadable}`,
      onClick: () =>
        isNotNil(props.vehicle.sourcingVehicle.url?.full) &&
        openInNewTab(props.vehicle.sourcingVehicle.url.full),
    })
    .add(
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultMobileDe'),
        serverName: 'mobile_de',
        testId: 'mobileDeRelativeFilters',
      })
    )
    .when(targetCountryCode === 'CZE', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultSauto'),
        serverName: 'sauto',
        testId: 'sautoRelativeFilters',
      })
    )
    .when(
      targetCountryCode && ['DEU', 'ITA', 'AUT', 'BEL', 'NLD'].includes(targetCountryCode),
      () =>
        createAdServerSearchMenuItem({
          label: i18n.t('general.actions.openResultAutoscout'),
          serverName: 'autoscout24',
          testId: 'autoscoutRelativeFilters',
        })
    )
    .when(targetCountryCode === 'POL', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultOtomoto'),
        serverName: 'otomoto',
        testId: 'otomotoRelativeFilters',
      })
    )
    .when(targetCountryCode === 'SVK', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultAutobazarEU'),
        serverName: 'autobazar_eu',
        testId: 'autobazarEuRelativeFilters',
      })
    )
    .when(targetCountryCode === 'ROU', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultAutovit'),
        serverName: 'autovit_ro',
        testId: 'autovitRelativeFilters',
      })
    )
    .when(targetCountryCode === 'FRA', () =>
      createAdServerSearchMenuItem({
        label: i18n.t('general.actions.openResultLacentrale'),
        serverName: 'lacentrale',
        testId: 'lacentraleRelativeFilters',
      })
    )
    .add({
      leftIcon: 'social/poll',
      label: i18n.t('entity.sourcing.labels.getPriceReport'),
      isLoading: isPriceReportCreating,
      onClick: createPriceReport,
    })
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE && !isVehicleHidden, {
      leftIcon: 'action/visibility_off',
      label: i18n.t('general.actions.hide'),
      onClick: handleHideButtonClick,
    })
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE && isVehicleHidden, {
      leftIcon: 'action/visibility',
      label: i18n.t('general.actions.unhide'),
      onClick: handleHideButtonClick,
    })
    .when(sourcingPage === SourcingPage.COMPARISON_PAGE, {
      leftIcon: 'action/delete_outline',
      label: i18n.t('general.actions.removeFromList'),
      onClick: handleComparisonButtonClick,
    });

  const firstMenuItem = head(menuItems);

  const actions: Action[] = [
    {
      type: 'dropdown-iconButton',
      icon: 'navigation/more_horiz',
      priority: 'secondary',
      menuItems,
    },
  ];

  return (
    <HStack spacing={1} align="flex-start">
      <IconButton
        size="small"
        severity={props.vehicle?.favourite ? 'warning' : undefined}
        icon="toggle/star"
        isDisabled={props.vehicle?.favourite ? false : props.isDisabled}
        data-testid={suffixTestId('favouriteButton', props)}
        onClick={handleFavouriteButtonClick}
      />

      <IconButton
        size="small"
        severity={props.vehicle?.inCart ? 'informational' : undefined}
        icon="action/shopping_cart"
        isDisabled={props.vehicle?.inCart ? false : props.isDisabled}
        data-testid={suffixTestId('cartButton', props)}
        onClick={handleShopButtonClick}
      />

      <Hide when={sourcingPage === SourcingPage.COMPARISON_PAGE}>
        <IconButton
          size="small"
          severity={props.vehicle?.comparison ? 'success' : undefined}
          icon="action/compare_arrows"
          isDisabled={props.vehicle?.comparison ? false : props.isDisabled}
          data-testid={suffixTestId('comparisonButton', props)}
          onClick={handleComparisonButtonClick}
        />

        <IconButton
          size="small"
          icon={props.vehicle?.hidden ? 'action/visibility' : 'action/visibility_off'}
          severity={props.vehicle?.hidden ? 'danger' : undefined}
          isDisabled={props.vehicle?.hidden ? false : props.isDisabled}
          data-testid={suffixTestId('unhideButton', props)}
          onClick={handleHideButtonClick}
        />
      </Hide>

      {menuItems.length === 1 &&
        isNotNil(firstMenuItem) &&
        isNotNil(firstMenuItem.leftIcon) &&
        isNotNil(firstMenuItem.onClick) && (
          <IconButton
            size="small"
            icon={firstMenuItem.leftIcon}
            severity={firstMenuItem.severity}
            isDisabled={firstMenuItem.isDisabled}
            onClick={firstMenuItem.onClick}
            data-testid={suffixTestId('actionButton', props)}
          />
        )}
      <Show when={menuItems.length > 1}>
        <Actions size="small" actions={actions} data-testid={suffixTestId('actions', props)} />
      </Show>

      <Show
        when={sourcingType === SourcingType.CLASSIFIEDS}
        whenFeatureEnabled={featureFlags.SOURCING_CARVAGO_BUY}
      >
        <IconButton
          size="small"
          priority="secondary"
          severity={props.vehicle?.carvagoBuy ? 'informational' : undefined}
          icon="custom/carvago"
          isDisabled={props.vehicle?.carvagoBuy || props.isDisabled}
          data-testid={suffixTestId('carvagoBuyButton', props)}
          onClick={handleCarvagoBuyButtonClick}
        />
      </Show>

      <Hide when={sourcingPage === SourcingPage.COMPARISON_PAGE || isCurrentDetailPage}>
        <Link
          to={
            isNotNil(props.vehicle?.sourcingVehicle?.adId)
              ? composePath(
                  sourcingType === SourcingType.AUCTIONS
                    ? sourcingRoutes.auctionDetail
                    : sourcingRoutes.vehicleDetail,
                  {
                    params: {adId: props.vehicle?.sourcingVehicle?.adId},
                  }
                )
              : ''
          }
          data-testid={suffixTestId('detailLink', props)}
        >
          <IconButton
            size="small"
            priority="primary"
            icon="hardware/keyboard_arrow_right"
            onClick={handleDetailButtonClick}
            data-testid={suffixTestId('detailButton', props)}
          />
        </Link>
      </Hide>

      <ConfirmDialog
        text={i18n.t('entity.vehicle.labels.reallyHide')}
        onConfirm={handleConfirmHide}
        onClose={close}
        isOpen={isOpen}
        data-testid={testIds.sourcing.classifieds('actionButtonsVehicle-hideConfirmDialog')}
      />

      <SourcingCarvagoBuyDialog
        isOpen={isCarvagoBuyOpen}
        onClose={closeCarvagoBuy}
        onConfirm={handleConfirmCarvagoBuy}
        data-testid={testIds.sourcing.classifieds('actionButtonsVehicle-carvagoBuyDialog')}
      />

      {relativeFiltersPresetEditor.presetEditorDialog}
    </HStack>
  );
}
